import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/utils';
import { GraficosCompras, IndicadoresCompras } from './BlocoCompras';
import { CartaoFolha, IndicadorFolha } from './BlocoFolha';
import { CartaoMetais, IndicadorMetais } from './BlocoMetais';
import ContinuarDeOndeParou from './ContinuarDeOndeParou';
import VitrineModulos from './VitrineModulos';
import { useAlcanca, usePainelMetais, useResumoCompras, useResumoFolha } from './consultas';

const hoje = new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

function saudacao(agora: Date): string {
  const hora = agora.getHours();
  return hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';
}

/**
 * A página inicial: onde cada coisa parou, os números que interessam no dia a
 * dia e a porta de cada módulo. Cada bloco só aparece para quem tem o módulo e
 * a permissão, e carrega sozinho — um módulo lento não segura os outros.
 */
export default function InicioPage() {
  const nome = useAuthStore((estado) => estado.user?.nome ?? '');

  const veCompras = useAlcanca('cotacao', 'cotacao.ver');
  const veFolha = useAlcanca('folha', 'folha.ver');
  const veMetais = useAlcanca('indices', 'indices.ver');

  const compras = useResumoCompras(veCompras);
  const folha = useResumoFolha(veFolha);
  const metais = usePainelMetais(veMetais);

  const agora = new Date();
  const dataDeHoje = hoje.format(agora);
  const temPendencias = veCompras || veFolha;
  const temAlgum = veCompras || veFolha || veMetais;

  return (
    <div className="px-lg py-lg">
      <header className="mb-lg">
        <h1 className="text-[22px] font-bold text-ink">
          {saudacao(agora)}, {nome.split(' ')[0]}
        </h1>
        <p className="mt-xs text-[13px] text-muted">{dataDeHoje.charAt(0).toUpperCase() + dataDeHoje.slice(1)}</p>
      </header>

      <div className="space-y-lg">
        {temAlgum && (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-md">
            {veCompras && <IndicadoresCompras dados={compras.data} carregando={compras.isLoading} />}
            {veMetais && <IndicadorMetais dados={metais.data} carregando={metais.isLoading} />}
            {veFolha && <IndicadorFolha dados={folha.data} carregando={folha.isLoading} />}
          </div>
        )}

        {(temPendencias || veMetais) && (
          <div className="grid items-start gap-md lg:grid-cols-3">
            {temPendencias && (
              <ContinuarDeOndeParou
                className={cn(veMetais ? 'lg:col-span-2' : 'lg:col-span-3')}
                compras={{ alcanca: veCompras, dados: compras.data, carregando: compras.isLoading, erro: compras.isError }}
                folha={{ alcanca: veFolha, dados: folha.data, carregando: folha.isLoading, erro: folha.isError }}
              />
            )}
            {veMetais && <CartaoMetais dados={metais.data} carregando={metais.isLoading} erro={metais.isError} />}
          </div>
        )}

        {veCompras && (
          <div className="grid items-stretch gap-md lg:grid-cols-3">
            <GraficosCompras dados={compras.data} carregando={compras.isLoading} erro={compras.isError} />
          </div>
        )}

        {veFolha && <CartaoFolha dados={folha.data} carregando={folha.isLoading} erro={folha.isError} />}

        {!temAlgum && (
          <p className="rounded-lg border border-dashed border-border bg-surface px-lg py-lg text-center text-[13px] text-muted">
            Seu usuário ainda não tem acesso a nenhum módulo. Peça ao administrador da empresa para liberar o que você usa.
          </p>
        )}

        <VitrineModulos />
      </div>
    </div>
  );
}
